import { useMemo } from 'react'
import styled from 'styled-components'

import { fromMd } from '@syconium/little-miss-figgy'

import { ContentfulColoredText } from '../../../../../app/_components/pages/contentful/components/ContentfulColoredText'
import {
  PricingRange,
  derivePricingForVariant,
} from '../../../../../app/_components/pricing/PricingRange.client'
import { usePortal } from '../../../../../app/_providers/PortalProvider.client'
import { useSiteWideMessaging } from '../../../../../app/_providers/SiteWideMessagingProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { EmbroideryService } from '../../../../../containers/EmbroideryService'
import { PromoText } from '../DetailsSection/styles'
import { usePdpServerSideProps } from '../PdpServerSidePropsProvider'
import { DetailsSectionSidePaddedSection } from '../styles'

import { BadgesSection, PriceDetails } from './ProductHighlights.styles'
import { StyleLinks } from './StyleLinks'

export const ProductHighlights = () => {
  const {
    cart: { finalSale: finalSaleText },
  } = useTranslation()
  const { portal } = usePortal()
  const { siteWidePromotionMessage } = useSiteWideMessaging()
  const { data: pdpServerSideProps } = usePdpServerSideProps()
  const { isEmbroidered } = EmbroideryService.useContainer()

  const handle = pdpServerSideProps?.product.handle

  const badges = useMemo(() => {
    const allBadges = pdpServerSideProps?.product.variantGroup.pdpBadges ?? []
    return allBadges.filter(badge => badge.id.toLowerCase() !== 'final sale')
  }, [pdpServerSideProps?.product.variantGroup.pdpBadges])

  const isFinalSale = !!pdpServerSideProps?.product.variantGroup.finalSale

  const productSpecificPromoText = useMemo(() => {
    return pdpServerSideProps?.productView?.promotionalColoredTextSegments ?? []
  }, [pdpServerSideProps?.productView?.promotionalColoredTextSegments])

  const siteWidePromoText = useMemo(() => {
    // If a portal color is selected & the portal has an active discount percentage, don't display sitewide promo text
    if (pdpServerSideProps?.product.variantGroup?.isPortalColor && portal?.discountPercentage) {
      return undefined
    }
    // If the product is site-wide promo eligible, display the site-wide promo text
    if (
      pdpServerSideProps?.product.variantGroup.siteWidePromoEligible &&
      siteWidePromotionMessage
    ) {
      return siteWidePromotionMessage
    }
    return undefined
  }, [
    pdpServerSideProps?.product.variantGroup?.isPortalColor,
    pdpServerSideProps?.product.variantGroup.siteWidePromoEligible,
    portal?.discountPercentage,
    siteWidePromotionMessage,
  ])

  const pricing = useMemo(() => {
    return pdpServerSideProps?.product.variantGroup.variant
      ? derivePricingForVariant(pdpServerSideProps.product.variantGroup.variant)
      : undefined
  }, [pdpServerSideProps?.product.variantGroup.variant])

  return (
    <DetailsSectionSidePaddedSection>
      <PriceDetails>
        {pricing ? (
          <PricingRange
            {...pricing}
            renderPriceText={priceContent => (
              <SecondaryTextColor>{priceContent}</SecondaryTextColor>
            )}
          />
        ) : null}
        {isFinalSale || isEmbroidered ? (
          <BadgesSection badges={[{ id: 'final sale', displayValue: finalSaleText }]} />
        ) : null}
      </PriceDetails>

      {productSpecificPromoText.length > 0 ? (
        <StyledContentfulColoredText segments={productSpecificPromoText} />
      ) : siteWidePromoText ? (
        <PromoText data-testid='promo-text' $extendedBottomMargin={badges.length > 0}>
          {siteWidePromoText}
        </PromoText>
      ) : null}
      {badges.length > 0 && <BadgesSection badges={badges} />}
      {handle ? <StyleLinks productGroupHandle={handle} /> : null}
    </DetailsSectionSidePaddedSection>
  )
}

const SecondaryTextColor = styled.span`
  color: ${o => o.theme.color.text.secondary.on.background};
`

const StyledContentfulColoredText = styled(ContentfulColoredText)`
  ${fromMd} {
    margin-top: ${o => o.theme.spacing(2)};
  }
  margin-bottom: ${o => o.theme.spacing(1)};
`
